import React from 'react';

import ColumnPicker from './ColumnPicker';
import ResizeHandle from './ResizeHandle';
import useTablePreferences from './useTablePreferences';
import {
  EMPTY_PREFERENCES,
  applyPreferences,
  orderedColumns,
  setWidth,
} from './preferences';

// Anything on screen can be resized, picker-listed or not — the handle is in
// the header. Except a minViewportWidth column, whose label preflight-tables
// also renders outside the header, where the handle would be adrift.
const isResizable = column =>
  true !== column.hidden && !column.minViewportWidth;

// Pair with useTablePreferences in components that return early before their
// columns exist; calling the combined hook after that would be conditional.
export const buildCustomizableColumns = ({ columns, preferences, save }) => {
  const applied = applyPreferences(columns, preferences);

  // Source columns, not `applied` — see ColumnPicker.
  const picker = (
    <ColumnPicker
      columns={orderedColumns(columns, preferences?.order)}
      preferences={preferences}
      onChange={save}
      onReset={() => save(EMPTY_PREFERENCES)}
    />
  );

  const decorated = applied.map(column => {
    if (!isResizable(column)) { return column; }

    return {
      ...column,
      headerStyle: { ...column.headerStyle, position: 'relative' },
      // headerTitle goes on the rendered header only — the picker keeps the
      // plain `label`, which it needs as text for its checkbox and move buttons.
      label: (
        <>
          {column.headerTitle ?
            <span title={column.headerTitle}>{column.label}</span> :
            column.label}
          <ResizeHandle
            width={column.width}
            onResize={width => save(setWidth(preferences, column.key, width))}
          />
        </>
      ),
    };
  });

  // `applied`, not `decorated`: an export wants the plain label rather than the
  // header markup wrapping it around a resize handle.
  return { columns: decorated, picker, exportColumns: applied };
};

const useCustomizableColumns = ({ tableKey, columns }) => {
  const { preferences, save } = useTablePreferences(tableKey);

  return buildCustomizableColumns({ columns, preferences, save });
};

export default useCustomizableColumns;
